/**
 * Required public host for Nango API (self-hosted or region-specific).
 * Never omit this — unset would fall back to Nango US Cloud and break
 * AU/EU/UK/CA data-residency. Set `NEXT_PUBLIC_NANGO_HOST`
 * (e.g. https://nango.yourdomain.com or https://api.nango.dev).
 */
export function getNangoHost(): string | undefined {
  const host = process.env.NEXT_PUBLIC_NANGO_HOST?.trim();
  return host || undefined;
}

const NANGO_CLOUD_CONNECT_UI = 'https://connect.nango.dev';

/**
 * Connect UI iframe origin. Distinct from the API host on Nango Cloud —
 * `api.nango.dev` sets `X-Frame-Options: SAMEORIGIN` and cannot be framed.
 *
 * Override with `NEXT_PUBLIC_NANGO_CONNECT_URL` for self-hosted Connect UI.
 * Otherwise: map cloud `api*.nango.dev` → `connect*.nango.dev`; self-hosted
 * deployments reuse the API host.
 */
export function getNangoConnectBaseUrl(apiHost: string): string {
  const override = process.env.NEXT_PUBLIC_NANGO_CONNECT_URL?.trim();
  if (override) return override;

  try {
    const url = new URL(apiHost);
    if (url.hostname === 'api.nango.dev') {
      return NANGO_CLOUD_CONNECT_UI;
    }
    // Regional cloud hosts, e.g. api.eu.nango.dev → connect.eu.nango.dev
    if (url.hostname.startsWith('api.') && url.hostname.endsWith('.nango.dev')) {
      url.hostname = `connect.${url.hostname.slice('api.'.length)}`;
      url.pathname = '/';
      url.search = '';
      url.hash = '';
      return url.origin;
    }
  } catch {
    // Invalid URL — fall through to apiHost (openConnectUI will fail closed).
  }

  return apiHost;
}
